import { Priority } from '@enview/interface/types/triage/Priority';
import { WorkflowStatus } from '@enview/interface/types/triage/WorkflowStatus';
import { BillTriageEvent } from '@enview/interface/types/triage/BillTriageEvent';
import {
  BillDiffUpdateSummary,
  BillMentionUpdate,
  BillUpdateSource,
} from '@enview/interface/types/triage/BillUpdateIndicator';
import base, { ApiTag } from './base';
import {
  ASSIGNED_BILLS_TAG,
  NEW_BILLS_COUNT_TAG,
  UPDATED_BILLS_COUNT_TAG,
} from './bill';

const billTriageAPI = base.injectEndpoints({
  endpoints: (build) => ({
    getNewBillsCount: build.query<
      { count: number; latestNewBillAt: string | null },
      { orgId: number }
    >({
      query: ({ orgId }) => ({
        url: `/v2/organizations/${orgId}/bills/new-count`,
        method: 'GET',
      }),
      providesTags: [NEW_BILLS_COUNT_TAG],
    }),
    // A null status clears the bill's Workflow Status in that workspace.
    updateWorkflowStatus: build.mutation<
      void,
      { orgId: number; billId: string; teamId: number; status: WorkflowStatus | null }
    >({
      query: ({ orgId, billId, teamId, status }) => ({
        url: `/v2/organizations/${orgId}/bills/${billId}/workflow-status`,
        method: 'PUT',
        data: { teamId, status },
      }),
      invalidatesTags: (result, error, { billId }) => [
        { type: ApiTag.BILL_TAG, id: billId },
        NEW_BILLS_COUNT_TAG,
      ],
    }),
    // A null status clears Workflow Status for every listed bill in that workspace.
    bulkUpdateWorkflowStatus: build.mutation<
      void,
      {
        orgId: number;
        teamId: number;
        billIds: string[];
        status: WorkflowStatus | null;
      }
    >({
      query: ({ orgId, teamId, billIds, status }) => ({
        url: `/v2/organizations/${orgId}/bills/bulk-workflow-update`,
        method: 'POST',
        data: { teamId, billIds, status },
      }),
      invalidatesTags: (result, error, { billIds }) => [
        ...billIds.map((billId) => ({ type: ApiTag.BILL_TAG, id: billId })),
        NEW_BILLS_COUNT_TAG,
      ],
    }),
    assignBill: build.mutation<
      void,
      { orgId: number; billId: string; teamId: number; assigneeUserIds: number[] }
    >({
      query: ({ orgId, billId, teamId, assigneeUserIds }) => ({
        url: `/v2/organizations/${orgId}/bills/${billId}/assigned`,
        method: 'PUT',
        data: { teamId, assigneeUserIds },
      }),
      invalidatesTags: (result, error, { billId }) => [
        { type: ApiTag.BILL_TAG, id: billId },
        ASSIGNED_BILLS_TAG,
      ],
    }),
    // Reuses the same bulk-workflow-update endpoint as bulkUpdateWorkflowStatus —
    // the backend accepts status/assigneeUserIds/priority independently in one call.
    bulkAssignBills: build.mutation<
      void,
      { orgId: number; teamId: number; billIds: string[]; assigneeUserIds: number[] }
    >({
      query: ({ orgId, teamId, billIds, assigneeUserIds }) => ({
        url: `/v2/organizations/${orgId}/bills/bulk-workflow-update`,
        method: 'POST',
        data: { teamId, billIds, assigneeUserIds },
      }),
      invalidatesTags: (result, error, { billIds }) => [
        ...billIds.map((billId) => ({ type: ApiTag.BILL_TAG, id: billId })),
        ASSIGNED_BILLS_TAG,
      ],
    }),
    updateBillPriority: build.mutation<
      void,
      { orgId: number; billId: string; teamId: number; priority: Priority }
    >({
      query: ({ orgId, billId, teamId, priority }) => ({
        url: `/v2/organizations/${orgId}/bills/${billId}/priority`,
        method: 'PUT',
        data: { teamId, priority },
      }),
      invalidatesTags: (result, error, { billId }) => [
        { type: ApiTag.BILL_TAG, id: billId },
      ],
    }),
    // Reuses the same bulk-workflow-update endpoint as bulkUpdateWorkflowStatus/
    // bulkAssignBills — the backend accepts status/assigneeUserIds/priority
    // independently in one call.
    bulkUpdatePriority: build.mutation<
      void,
      { orgId: number; teamId: number; billIds: string[]; priority: Priority }
    >({
      query: ({ orgId, teamId, billIds, priority }) => ({
        url: `/v2/organizations/${orgId}/bills/bulk-workflow-update`,
        method: 'POST',
        data: { teamId, billIds, priority },
      }),
      invalidatesTags: (result, error, { billIds }) =>
        billIds.map((billId) => ({ type: ApiTag.BILL_TAG, id: billId })),
    }),
    setBillWorkspaces: build.mutation<
      void,
      { orgId: number; billId: string; teamIds: number[] }
    >({
      query: ({ orgId, billId, teamIds }) => ({
        url: `/v2/organizations/${orgId}/bills/${billId}/workspaces`,
        method: 'PUT',
        data: { teamIds },
      }),
      // Removing a bill from a workspace also removes it from that workspace's New Bills.
      invalidatesTags: (result, error, { billId }) => [
        { type: ApiTag.BILL_TAG, id: billId },
        NEW_BILLS_COUNT_TAG,
      ],
    }),
    getBillUpdateIndicators: build.query<
      {
        diffs: BillDiffUpdateSummary[];
        mentions: BillMentionUpdate[];
        sources: BillUpdateSource[];
      },
      { orgId: number; billId: string }
    >({
      query: ({ orgId, billId }) => ({
        url: `/v2/organizations/${orgId}/bills/${billId}/update-indicators`,
        method: 'GET',
      }),
      providesTags: (result, error, { billId }) => [
        { type: ApiTag.BILL_TAG, id: billId },
      ],
    }),
    // Marks every unread update on the bills as seen across the user's workspaces in the
    // org. Returns the created seen-state ids for undoMarkBillUpdatesSeen, and whether the
    // bills left Updated Bills.
    markAllBillUpdatesSeen: build.mutation<
      { seenStateIds: number[]; leftUpdatedBills: boolean },
      { orgId: number; billIds: string[] }
    >({
      query: ({ orgId, billIds }) => ({
        url: `/v2/organizations/${orgId}/bills/update-indicators/mark-all-seen`,
        method: 'POST',
        data: { billIds },
      }),
      invalidatesTags: (result, error, { billIds }) => [
        ...billIds.map((billId) => ({ type: ApiTag.BILL_TAG, id: billId })),
        UPDATED_BILLS_COUNT_TAG,
      ],
    }),
    undoMarkBillUpdatesSeen: build.mutation<
      void,
      { orgId: number; billIds: string[]; seenStateIds: number[] }
    >({
      // billIds is only used to refresh the bills' cached rows.
      query: ({ orgId, seenStateIds }) => ({
        url: `/v2/organizations/${orgId}/bills/update-indicators/undo-mark-seen`,
        method: 'POST',
        data: { seenStateIds },
      }),
      invalidatesTags: (result, error, { billIds }) => [
        ...billIds.map((billId) => ({ type: ApiTag.BILL_TAG, id: billId })),
        UPDATED_BILLS_COUNT_TAG,
      ],
    }),
    getTriageEvents: build.query<
      BillTriageEvent[],
      { orgId: number; billId: string; teamId?: number }
    >({
      query: ({ orgId, billId, teamId }) => ({
        url: `/v2/organizations/${orgId}/bills/${billId}/triage-events${
          teamId ? `?teamId=${teamId}` : ''
        }`,
        method: 'GET',
      }),
      providesTags: (result, error, { billId }) => [
        { type: ApiTag.BILL_TAG, id: billId },
      ],
    }),
  }),
  overrideExisting: false,
});

export default billTriageAPI;
